//region :: Imports ::
import React, {ReactElement, ReactNode} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {MCancelIconName, MOkIconName} from '../Toolkit/Icons';
import {DialogPositionType} from 'primereact/dialog';
import Dialog from '../Toolkit/Components/Controls/Dialog';
import MButton from '../Toolkit/Components/Controls/Button';
import {MInputValueType} from '../Toolkit/Components/Controls/TextArea';
import MTextBox from '../Toolkit/Components/Controls/TextBox';
import MFormControl from '../Toolkit/Components/Controls/FormControl';
import {Invoice} from '../Api/ControllerTypes/InvoiceTypes';
import MCalendar from '../Components/Toolkit/Calendar';
import MNumericBox from '../Components/Toolkit/NumericBox';
import {Enumeration} from '../Api/ControllerTypes/EnumerationTypes';
import MDropDown from '../Toolkit/Components/Controls/DropDown';
import {MNullable} from '../Toolkit/Utils';
import {MDropDownItem} from '../Toolkit/Components/Controls/DropDownOption';

//endregion

/** */
type Props = {
    councilList: Enumeration[];
    feeTypes: Enumeration[];
}

/** */
type State = {
    position?: DialogPositionType;
    visible: boolean;
    newData: boolean;
    invoiceData: Invoice;
    onUpdate?: (newData: boolean, data: Invoice) => void;
}

/** */
class InvoiceDialog extends MComponent<Props, State> {
    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();
        this.initializeState();
    }

    /** */
    private bindMethods(): void {
        this.dialog_onHide = this.dialog_onHide.bind(this);

        this.getDialogTemplate = this.getDialogTemplate.bind(this);
        this.getFooterTemplate = this.getFooterTemplate.bind(this);
        this.ok_Click = this.ok_Click.bind(this);
        this.onStartDateChange = this.onStartDateChange.bind(this);
        this.onEndDateChange = this.onEndDateChange.bind(this);
        this.onPoValueChange = this.onPoValueChange.bind(this);
        this.onChargeChange = this.onChargeChange.bind(this);
        this.onInvoiceValueChange = this.onInvoiceValueChange.bind(this);
        this.onNoteValueChange = this.onNoteValueChange.bind(this);
        this.onCouncilValueChange = this.onCouncilValueChange.bind(this);
        this.onApplicationSentValueChange = this.onApplicationSentValueChange.bind(this);
        this.onInvoicedValueChange = this.onInvoicedValueChange.bind(this);
        this.onFeeTypeValueChange = this.onFeeTypeValueChange.bind(this);

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
    }

    /** */
    private initializeState(): void {
        this.state = {
            visible: false,
            newData: false,
            invoiceData: {
                id: 0,
                parentId: '',
                applicationSent: false,
                poNumber: '',
                workStart: new Date(),
                workEnd: new Date(),
                charge: 0,
                invoiceNumber: '',
                invoiced: false,
                councilId: 0,
                feeType: 0,
                notes: '',
                modifiedBy: '',
                modifiedOn: new Date()
            }
        };
    }

    /** */
    private dialog_onHide(): void {
        this.safeSetState((draft: State) => draft.visible = false);
    }

    /** */
    public show(newData: boolean, currentData: Invoice, inputCallback: (newData: boolean, data:Invoice) => void, position?: DialogPositionType) {
        this.safeSetState((draft: State) => {
            draft.position = position;
            draft.visible = true;
            draft.newData = newData;
            draft.onUpdate = inputCallback;
            draft.invoiceData = currentData;
        });
    }

    public hide() {
        this.safeSetState((draft: State) => {
            draft.visible = false;
        });
    }

    private ok_Click() {
        this.state.onUpdate!(this.state.newData, this.state.invoiceData!);
        this.hide();
    }

    private getDialogTemplate(): ReactNode {
        const councilRows = this.props.councilList.map(council => ({
            value: council.id,
            label: council.name
        }));

        const feeTypes = this.props.feeTypes.map(feeType => ({
            value: feeType.id,
            label: feeType.name
        }));

        const yesNoList: MDropDownItem<boolean>[] = [{label: 'No', value: false},{label: 'Yes', value: true}];

        return (
            <div className="formgrid grid">
                <div className="col-12 md:col-6">
                    <MFormControl label={'PO Number'} id='PoNumberField'>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id={'poNumberValue'}
                                          maxLength={Invoice.PoNumberMaxLength}
                                          disabled={true}
                                          initialValue={this.state.invoiceData?.poNumber}
                                          onChange={this.onPoValueChange}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'Charge'} id={'ChargeField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MNumericBox id={'chargeValue'}
                                             maxLength={9}
                                             disabled={false}
                                             className={fieldClasses}
                                             decimalPlaces={2}
                                             mode={'currency'}
                                             currency={'GBP'}
                                             useGrouping={false}
                                             onChange={this.onChargeChange}
                                             initialValue={this.state.invoiceData?.charge}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'Start Date'} id={'StartDateField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MCalendar id={"startDateValue"}
                                           disabled={false}
                                           className={fieldClasses}
                                           showTime={false}
                                           hourFormat={'24'}
                                           onChange={this.onStartDateChange}
                                           initialValue={this.state.invoiceData?.workStart!}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'End Date'} id={'EndDateField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MCalendar id={"endDateValue"}
                                           disabled={false}
                                           className={fieldClasses}
                                           showTime={false}
                                           hourFormat={'24'}
                                           onChange={this.onEndDateChange}
                                           initialValue={this.state.invoiceData?.workEnd!}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'Council'} id={'councilField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown id={'councilValue'}
                                           disabled={false}
                                           items={councilRows}
                                           initialValue={this.state.invoiceData?.councilId}
                                           onChange={this.onCouncilValueChange}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'FeeType'} id={'feeType'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown id={'feeTypeValue'}
                                           disabled={false}
                                           items={feeTypes}
                                           initialValue={this.state.invoiceData?.feeType}
                                           onChange={this.onFeeTypeValueChange}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'Application Sent'}
                                  id={'applicationSentField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown<boolean> id='applicationSentValue'
                                                    disabled={false}
                                                    items={yesNoList}
                                                    initialValue={this.state.invoiceData?.applicationSent}
                                                    onChange={this.onApplicationSentValueChange}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'Invoice Number'} id={'invoiceNumberField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id={'invoiceNumberValue'}
                                    maxLength={Invoice.InvoiceMaxLength}
                                    disabled={false}
                                    initialValue={this.state.invoiceData?.invoiceNumber}
                                    onChange={this.onInvoiceValueChange}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'Invoice Received'}
                                  id={'invoicedField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown<boolean> id='invoicedValue'
                                                    disabled={false}
                                                    items={yesNoList}
                                                    initialValue={this.state.invoiceData?.invoiced}
                                                    onChange={this.onInvoicedValueChange}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-12">
                    <MFormControl label={'Note'} id={'NoteField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id={'noteValue'}
                                          maxLength={Invoice.NoteMaxLength}
                                          disabled={false}
                                          initialValue={this.state.invoiceData?.notes}
                                          onChange={this.onNoteValueChange}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
            </div>
        );
    }

    private getFooterTemplate(): ReactNode {
        return (
            <div>
                <MButton label="OK"
                         color="success"
                         icon={MOkIconName}
                         disabled={false}
                         onClick={this.ok_Click}/>
                <MButton label={'Cancel'}
                         icon={MCancelIconName}
                         disabled={false}
                         onClick={this.hide}/>
            </div>
        );
    }

    private onStartDateChange(newValue: Date | Date [] | undefined) {
        this.safeSetState(
            (draft: State) =>
                draft.invoiceData!.workStart = Array.isArray(newValue)
                                               ? newValue[0]
                                               : newValue as Date);
    }

    private onEndDateChange(newValue: Date | Date[] | undefined) {
        this.safeSetState(
            (draft: State) =>
                draft.invoiceData!.workEnd = Array.isArray(newValue)
                                             ? newValue[0]
                                             : newValue as Date);
    }

    private onChargeChange(newValue: number | undefined) {
        this.safeSetState(
            (draft: State) => draft.invoiceData!.charge = newValue!);
    }
    private onPoValueChange(newValue: MInputValueType) {
        this.safeSetState(
            (draft: State) => draft.invoiceData!.poNumber = newValue.toString());
    }

    private onInvoiceValueChange(newValue: MInputValueType) {
        this.safeSetState(
            (draft: State) => draft.invoiceData!.invoiceNumber = newValue.toString());
    }

    private onCouncilValueChange(newValue: MNullable<number>) {
        if(newValue !== null) {
            this.safeSetState(
                (draft: State) => draft.invoiceData!.councilId = newValue!);
        }
    }

    private onFeeTypeValueChange(newValue: MNullable<number>) {
        if(newValue !== null) {
            this.safeSetState(
                (draft: State) => draft.invoiceData!.feeType = newValue!);
        }
    }

    private onApplicationSentValueChange(newValue: MNullable<boolean>) {
        if(newValue !== null) {
            this.safeSetState(
                (draft: State) => draft.invoiceData!.applicationSent = newValue);
        }
    }

    private onInvoicedValueChange(newValue: MNullable<boolean>) {
        if(newValue !== null) {
            this.safeSetState(
                (draft: State) => draft.invoiceData!.invoiced = newValue);
        }
    }

    private onNoteValueChange(newValue: MInputValueType) {
        this.safeSetState(
            (draft: State) => draft.invoiceData!.notes = newValue.toString());
    }

    /** */
    public render(): ReactElement {

        return (
            <React.Fragment>
                <Dialog title={'Invoice Data'}
                        content={this.getDialogTemplate()}
                        icon={{name: 'pi pi-pencil', color: 'mcolor-info'}}
                        footer={this.getFooterTemplate()}
                        position={this.state.position}
                        canClose={false}
                        closeOnEscape={false}
                        visible={this.state.visible}
                        onHide={this.dialog_onHide}
                />
            </React.Fragment>

        );

    }

}

export default InvoiceDialog;
